import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import type { Point } from "@nivo/line"
import { Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { ChartsContainer } from "../../../../../components/graphs/ChartsContainer"
import { BaseLineChart } from "../../../../../components/graphs/Line/BaseLineChart"
import { getTheme } from "../../../../../hooks/UseTheme"
import type { ConfigFormikProps, DistributionChartProps } from "../../../Interfaces"

import styles from "./DistributionChart.module.scss"

/**
 * A component to display the distribution of the given data.
 *
 * It displays a line chart with the distribution of the given data.
 * It also displays the positive and negative thresholds chosen by the user.
 * The thresholds are displayed as dashed lines on the chart.
 * The user can hover over the chart to see the exact values of the data points.
 *
 * @param {DistributionChartProps} props - The props for the DistributionChart component.
 * @param {Serie[]} distributionChartData - The data to be displayed in the chart.
 * @param {string} title - The title of the chart.
 * @param {string} tooltipText - The text to be displayed in the tooltip.
 * @param {number} graphHeight - The height of the chart.
 * @param {boolean} isLoading - Whether the chart is in a loading state.
 * @returns {ReactElement} The rendered DistributionChart component.
 */
export function DistributionChart(props: Readonly<DistributionChartProps>): ReactElement {
  const { distributionChartData, title, tooltipText, graphHeight, isLoading } = props
  const {
    values: { positiveThreshold, negativeThreshold },
  } = useFormikContext<ConfigFormikProps>()

  const theme = getTheme()
  const { t } = useTranslation("ModelConfigurations")

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight}
      tooltipText={tooltipText}
      gutterBottom
    >
      <BaseLineChart
        data={distributionChartData}
        chartOptions={{
          margin: { top: 40, bottom: 46, left: 50, right: 20 },
          xScale: {
            type: "linear",
          },
          yScale: {
            type: "linear",
            stacked: false,
          },
          enableArea: true,
          areaOpacity: 0.5,
          legends: [
            {
              anchor: "top-left",
              direction: "row",
              justify: false,
              translateX: 0,
              translateY: -30,
              itemsSpacing: 60,
              itemDirection: "left-to-right",
              itemWidth: 80,
              itemHeight: 0,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: "square",
              effects: [
                {
                  on: "hover",
                  style: {
                    itemBackground: "rgba(0, 0, 0, .03)",
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ],
          axisBottom: {
            legend: t("charts.distribution.axis.score"),
            legendOffset: 36,
            legendPosition: "end",
          },
          axisTop: {
            legend: t("charts.distribution.axis.concentration"),
            legendOffset: -10,
            legendPosition: "start",
            format: () => "",
          },
          markers: [
            {
              axis: "x",
              value: positiveThreshold,
              legend: t("charts.distribution.markers.chosenPositive"),
              legendOrientation: "vertical",
              legendPosition: "left",
              lineStyle: {
                strokeWidth: 0.5,
                strokeDasharray: "10",
                stroke: theme.palette.blue.text[2],
              },
              textStyle: { fontSize: "12px", fontWeight: "400", fill: theme.palette.blue.text[2] },
            },
            {
              axis: "x",
              value: negativeThreshold,
              legend: t("charts.distribution.markers.chosenNegative"),
              legendOrientation: "vertical",
              legendPosition: "left",
              lineStyle: {
                strokeWidth: 0.5,
                strokeDasharray: "10",
                stroke: theme.palette.red.text[2],
              },
              textStyle: { fontSize: "12px", fontWeight: "400", fill: theme.palette.red.text[2] },
            },
          ],
          layers: ["axes", "crosshair", "grid", "legends", "mesh", "points", "lines", "areas", "slices", "markers"],
          sliceTooltip: ({ slice }) => {
            const formattedPoints: Point[] = []
            slice.points.forEach((point) => {
              const formattedPointName = point.id.substring(0, point.id.indexOf("("))
              if (formattedPoints.every((point) => !point.id.includes(formattedPointName))) {
                formattedPoints.push(point)
              }
            })

            return (
              <div className={`${styles.tooltip} chart-tooltip`}>
                <div className={styles.thresholdContainer}>
                  <Typography variant="p" gutterBottom>
                    {t("charts.distribution.tooltip.threshold")}
                  </Typography>
                  <div>
                    <Typography variant="label-bold">{t("labels.positive")}</Typography>
                    <Typography variant="label-bold" color={"important"} variantColor={2}>
                      {positiveThreshold}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="label-bold">{t("labels.negative")}</Typography>
                    <Typography variant="label-bold" color={"negative"} variantColor={2}>
                      {negativeThreshold}
                    </Typography>
                  </div>
                </div>
                <div className={styles.labelsContainer}>
                  <Typography variant="p" gutterBottom>
                    {t("charts.distribution.tooltip.labelTitle")}
                  </Typography>
                  {formattedPoints.map((point) => (
                    <div
                      key={point.id}
                      className={`${point.serieColor === theme.palette.red.text[2] ? styles.negMark : styles.posMark}`}
                    >
                      <Typography variant="label-bold">{point.id.substring(0, point.id.indexOf("("))}</Typography>
                      <Typography variant="label-bold">{point.data.yFormatted}</Typography>
                    </div>
                  ))}
                </div>
              </div>
            )
          },
          enableSlices: "y",
        }}
      />
    </ChartsContainer>
  )
}
